import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaBell,
  FaExternalLinkAlt,
  FaHome,
  FaSignOutAlt,
  FaUserShield,
} from "react-icons/fa";

import { useAsideMenu } from "../../contexts/AsideMenuContext";

const PAGE_TITLES = [
  {
    prefix: "/appli/messages",
    title: "Messages",
    subtitle: "Demandes reçues depuis le site",
  },
  {
    prefix: "/appli/actualites",
    title: "Actualités",
    subtitle: "Gestion des publications",
  },
  {
    prefix: "/appli/albums-photos",
    title: "Albums photos",
    subtitle: "Galeries et médias",
  },
  {
    prefix: "/appli/groupes",
    title: "Groupes",
    subtitle: "Cours, horaires et tarifs",
  },
  {
    prefix: "/appli/agenda",
    title: "Agenda",
    subtitle: "Événements associatifs",
  },
  {
    prefix: "/appli/palmares",
    title: "Palmarès",
    subtitle: "Résultats et distinctions",
  },
  {
    prefix: "/appli/section",
    title: "Association",
    subtitle: "Informations de la fiche publique",
  },
  {
    prefix: "/appli/utilisateurs",
    title: "Utilisateurs",
    subtitle: "Comptes et rôles",
  },
  {
    prefix: "/appli/sectionadmin",
    title: "Associations admin",
    subtitle: "Gestion globale des associations",
  },
  {
    prefix: "/appli/infrastructures",
    title: "Infrastructures",
    subtitle: "Équipements municipaux",
  },
  {
    prefix: "/appli/partenaires",
    title: "Partenaires",
    subtitle: "Soutiens et acteurs locaux",
  },
  {
    prefix: "/appli/settingsadmin",
    title: "Paramètres admin",
    subtitle: "Configuration du site",
  },
  {
    prefix: "/appli/visites",
    title: "Visites",
    subtitle: "Statistiques de fréquentation",
  },
];

const DashboardTopbar = ({ userInfo, sectionInfo }) => {
  const { toggleMenu } = useAsideMenu();
  const location = useLocation();
  const navigate = useNavigate();

  const pageMeta = useMemo(() => {
    return (
      PAGE_TITLES.find((item) => location.pathname.startsWith(item.prefix)) || {
        title: "Administration",
        subtitle: "Tableau de bord",
      }
    );
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/85">
      <div className="flex min-h-[76px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-4">
          <button
            type="button"
            onClick={toggleMenu}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-white lg:hidden"
            aria-label="Ouvrir le menu"
          >
            <FaBars />
          </button>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              {sectionInfo?.nom || "MonAppliAsso"}
            </p>
            <h1 className="truncate text-xl font-bold text-slate-950 dark:text-white sm:text-2xl">
              {pageMeta.title}
            </h1>
            <p className="hidden text-sm text-slate-500 dark:text-slate-300 sm:block">
              {pageMeta.subtitle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-white md:inline-flex"
          >
            <FaHome />
            Site public
            <FaExternalLinkAlt className="text-xs" />
          </button>

          <button
            type="button"
            className="relative flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-white"
            aria-label="Notifications"
          >
            <FaBell />
            <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-white dark:ring-slate-900" />
          </button>

          <div className="hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:flex">
            <img
              src={userInfo?.photoURL || "/images/user_avatar.jpg"}
              alt={`${userInfo?.prenom || ""} ${userInfo?.nom || ""}`}
              className="h-9 w-9 rounded-xl object-cover"
            />

            <div className="hidden min-w-0 lg:block">
              <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                {userInfo?.prenom} {userInfo?.nom}
              </p>
              <p className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-300">
                <FaUserShield />
                {Number(userInfo?.role_id) === 1
                  ? "Administrateur"
                  : "Responsable"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-red-50 hover:text-red-600 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:bg-red-500/10 dark:hover:text-red-300"
            aria-label="Se déconnecter"
          >
            <FaSignOutAlt />
          </button>
        </div>
      </div>
    </header>
  );
};

export default DashboardTopbar;
